import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";

/**
 * Public-pages top header — shows nav links + sign-in / get-started CTAs.
 * Drop this at the top of Login, Signup, Legal pages, About so users can
 * navigate to other public pages without scrolling back to landing.
 */
const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/marketplace", label: "Marketplace" },
  { to: "/explore", label: "Explore" },
  { to: "/about", label: "About" },
  { to: "/legal", label: "Legal" },
];

export default function PublicHeader({ hideAuthCTAs = false }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur" data-testid="public-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
        <Logo size="sm" />

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6 text-xs">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`transition-colors ${
                pathname === l.to
                  ? "text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`public-nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Right side CTAs */}
        <div className="flex items-center gap-2">
          {!hideAuthCTAs && (
            <>
              {pathname !== "/login" && (
                <Link to="/login" className="hidden sm:inline-flex">
                  <Button variant="ghost" size="sm" className="rounded-full text-xs" data-testid="public-signin-cta">
                    Sign in
                  </Button>
                </Link>
              )}
              {pathname !== "/signup" && (
                <Link to="/signup">
                  <Button size="sm" className="rounded-full text-xs h-8 px-4" data-testid="public-signup-cta">
                    Get started
                  </Button>
                </Link>
              )}
            </>
          )}
          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen((m) => !m)}
            className="md:hidden h-8 w-8 grid place-items-center rounded-lg hover:bg-muted"
            aria-label="Toggle menu"
            data-testid="public-mobile-menu"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Mobile nav drawer */}
      {mobileOpen && (
        <nav className="md:hidden border-t border-border bg-background/95 backdrop-blur" data-testid="public-mobile-nav">
          <div className="max-w-7xl mx-auto px-5 py-3 grid gap-1">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                className="text-sm py-2 px-2 rounded-lg hover:bg-muted text-foreground/90"
              >
                {l.label}
              </Link>
            ))}
            {!hideAuthCTAs && pathname !== "/login" && (
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                className="text-sm py-2 px-2 rounded-lg hover:bg-muted text-foreground/90"
              >
                Sign in
              </Link>
            )}
          </div>
        </nav>
      )}
    </header>
  );
}
